// --------------------------------------------------------------
// Created On: 2024-03-10
// Author: Zachary Thomas
//
// Last Modified: 2025-11-06
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useState, useEffect } from "react";
import ColorPicker from "../../../components/ColorPicker/ColorPicker";
import styles from "./ShoppingCartItem.module.scss";
import stringIsInteger from "../../../utilities/stringIsInteger";
import { MAX_ITEM_QUANTITY, MAX_IMPELLER_ANGLE, MAX_IMPELLER_TRIM } from "../../../constants/miscellaneous";
import LeadTime from "../../LeadTime/LeadTime";
import stringIsValidNumber from "../../../utilities/stringIsValidNumber";

// Component for showing information about a single cart item in a list.
export default function ShoppingCartItem(props: Props): Component {
  const paintColorIndex = props.paintColors.findIndex((paintColor) => paintColor.paintColorId === props.paintColorId);
  const paintColorSelected = paintColorIndex > -1;
  const [updatedQuantity, setUpdatedQuantity] = useState<string>("");
  const [updatedImpellerAngle, setUpdatedImpellerAngle] = useState<string | null>(null);
  const [updatedImpellerTrim, setUpdatedImpellerTrim] = useState<string | null>(null);
  const [isFullTrim, setIsFullTrim] = useState<boolean>(props.impellerTrim !== "" ? false : true);
  const quantityIsBetweenOneAndMax =
    props.quantity !== "" && parseInt(props.quantity, 10) > 0 && parseInt(props.quantity, 10) <= MAX_ITEM_QUANTITY;

  // Update quantity state when changes to quantity field are saved.
  useEffect(() => {
    setUpdatedQuantity(props.quantity);
  }, [props.quantity]);

  // Update impellerAngle state when changes to impellerAngle field are saved.
  useEffect(() => {
    setUpdatedImpellerAngle(props.impellerAngle);
  }, [props.impellerAngle]);

  // Update impellerTrim state when changes to impellerTrim field are saved.
  useEffect(() => {
    if (props.impellerTrim !== null && props.impellerTrim !== "") {
      setUpdatedImpellerTrim(props.impellerTrim);
    }
  }, [props.impellerTrim]);

  useEffect(() => {
    setIsFullTrim(
      props.impellerTrim !== "" &&
        props.impellerTrim !== null &&
        props.impellerTrim !== undefined &&
        parseFloat(props.impellerTrim) !== 0
        ? false
        : true
    );
  }, [props.impellerTrim]);

  // Validation function for quantity input.
  function updateQuantity(quantity: string) {
    if ((quantity === "" || parseInt(quantity, 10) > 0) && quantity.indexOf(".") === -1 && stringIsInteger(quantity)) {
      setUpdatedQuantity(quantity);
    }
  }
  // Validation function for impeller trim input.
  function updateImpellerTrim(impellerTrim: string) {
    if (impellerTrim === "" || stringIsValidNumber(impellerTrim)) {
      setUpdatedImpellerTrim(impellerTrim);
    }
  }

  function handleIsFullTrim(fullTrimStatus: string) {
    if (fullTrimStatus === "full-trim") {
      setIsFullTrim(true);
      updateImpellerTrim("0");
      props.onUpdateCartItem(
        props.cartItemId,
        props.quantity,
        props.quantityIsValid,
        props.paintColorId,
        "0",
        props.impellerTrimIsValid,
        props.impellerAngle,
        props.impellerAngleIsValid
      );
    } else {
      setIsFullTrim(false);
    }
  }

  // Validation function for impeller angle input.
  function updateImpellerAngle(impellerAngle: string) {
    if (impellerAngle === "" || stringIsInteger(impellerAngle)) {
      setUpdatedImpellerAngle(impellerAngle);
    }
  }

  // Function to validate all aspects of a cart item are within the acceptable bounds.
  function validateCartItem(
    cartItemId: number,
    quantity: string,
    paintColorId: number | null,
    impellerTrim: string | null,
    impellerAngle: string | null
  ) {
    let quantityIsValid = true;
    let impellerTrimIsValid = true;
    let impellerAngleIsValid = true;
    if (Number(quantity) > MAX_ITEM_QUANTITY || Number(quantity) <= 0) {
      quantityIsValid = false;
    }
    if (impellerAngle !== null) {
      if (Number(impellerAngle) < 0 || Number(impellerAngle) > MAX_IMPELLER_ANGLE || impellerAngle === "") {
        impellerAngleIsValid = false;
      }
    }
    if (impellerTrim !== null) {
      if (Number(impellerTrim) < 0 || Number(impellerTrim) > MAX_IMPELLER_TRIM || impellerTrim === "") {
        impellerTrimIsValid = false;
      }
    }

    props.onUpdateCartItem(
      cartItemId,
      quantity,
      quantityIsValid,
      paintColorId,
      impellerTrim,
      impellerTrimIsValid,
      impellerAngle,
      impellerAngleIsValid
    );
  }

  // Get discounted price of part.
  function getDiscountedPrice(price: number | null, quantity: string, companyDiscount?: number | null): string {
    if (price === null) {
      return "N/A";
    } else if (companyDiscount === null || companyDiscount === undefined) {
      return (Number(quantity) * price).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } else {
      const totalPrice = Number(quantity) * price;
      return (totalPrice - totalPrice * (companyDiscount / 100)).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
  }

  // Function that either calls the update list item or, if the quantity of the item is greater than one, lifts the state
  // necessary to display the quantity modal.
  function updatePaintColor(selectedColorId: number | null) {
    let paintColor = null;
    let paintColorId = null;
    if (selectedColorId !== null) {
      const selectedColorIndex = props.paintColors.findIndex(
        (paintColor) => paintColor.paintColorId === selectedColorId
      );
      paintColor = props.paintColors[selectedColorIndex];
      paintColorId = paintColor.paintColorId;
    }
    if (Number(props.quantity) > 1) {
      props.onUpdateMultiplePaintCartItems(props.cartItemId, paintColor);
    } else {
      props.onUpdateCartItem(
        props.cartItemId,
        props.quantity,
        props.quantityIsValid,
        paintColorId,
        props.impellerTrim,
        props.impellerTrimIsValid,
        props.impellerAngle,
        props.impellerAngleIsValid
      );
    }
  }

  return (
    <div data-test="shopping-cart-item-container" className="container p-0">
      <li className={styles.body}>
        <p data-test="shopping-cart-item-name" className={styles.partName}>
          {props.name}
        </p>
        <div className="row">
          <div className="col">
            <ul className={styles.content}>
              <li data-test="shopping-cart-item-part-number" className={styles.partNumber}>
                {props.partNumber}
              </li>
              {props.weight !== null && props.weight !== 0 ? (
                <li data-test="shopping-cart-item-weight" className={styles.partNumber}>
                  Weight:&nbsp;{props.weight}&nbsp;lbs
                </li>
              ) : (
                <li data-test="shopping-cart-item-weight" className={styles.partNumber}>
                  Weight:&nbsp;N/A
                </li>
              )}
              {props.isPaintable && (
                <li>
                  <div className={`${styles.row} row`}>
                    <div
                      data-test="shopping-cart-item-paint-name"
                      className={`col-12 col-lg-10 ${styles.cartAttribute}`}
                    >
                      Paint Color: {paintColorSelected ? props.paintColors[paintColorIndex].name : "No Paint"}
                    </div>
                    <div className="col-12 col-lg-10">
                      <ColorPicker
                        colors={props.paintColors}
                        selectedColorId={paintColorSelected ? props.paintColors[paintColorIndex].paintColorId : null}
                        disabled={false}
                        onChange={(selectedColorId) => updatePaintColor(selectedColorId)}
                      />
                    </div>
                  </div>
                </li>
              )}

              <li>
                <div>
                  <span>Ready To Ship:</span>
                </div>
                <LeadTime
                  className="justify-content-start pt-1"
                  quantity={updatedQuantity}
                  partNumber={props.partNumber}
                  onLeadTimeCalculated={(dateAvailable) =>
                    props.quoteId === undefined
                      ? props.onUpdateCartItemDateAvailable(props.cartItemId, dateAvailable)
                      : props.onUpdateQuoteCartItemDateAvailable(props.cartItemId, dateAvailable)
                  }
                  paintColorId={props.paintColorId}
                  isImpeller={props.impellerAngle === null || props.impellerTrim === null ? false : true}
                  impellerTrim={props.impellerTrim}
                  impellerAngle={props.impellerAngle}
                />
              </li>
              {props.partNumber.includes("-3-") && (
                <li>
                  <div className={`${styles.row} row`}>
                    <div className={`col-12 col-lg-10 ${styles.cartAttribute}`}>
                      <span>Impeller Trim:</span>
                    </div>
                    <div className="col-12 col-lg-9">
                      <select
                        className="form-select form-control my-1"
                        value={isFullTrim ? "full-trim" : "custom"}
                        onChange={(e) => {
                          handleIsFullTrim(e.currentTarget.value);
                        }}
                      >
                        <option value="full-trim">Full Trim</option>
                        <option value="custom">Custom</option>
                      </select>
                      {!isFullTrim && (
                        <input
                          className={`form-control mx-auto ${!props.impellerTrimIsValid ? styles.inputError : ""}`}
                          data-test="impeller-trim-input"
                          type="text"
                          maxLength={10}
                          value={updatedImpellerTrim || ""}
                          onChange={(e) => updateImpellerTrim(e.target.value)}
                          onBlur={() =>
                            validateCartItem(
                              props.cartItemId,
                              updatedQuantity,
                              props.paintColorId,
                              updatedImpellerTrim,
                              updatedImpellerAngle
                            )
                          }
                          onKeyUp={(e) => {
                            if (e.key === "Enter") {
                              validateCartItem(
                                props.cartItemId,
                                updatedQuantity,
                                props.paintColorId,
                                updatedImpellerTrim,
                                updatedImpellerAngle
                              );
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>
                </li>
              )}
              {!props.impellerTrimIsValid && (
                <div className="col-12 col-lg-9">
                  <span
                    className={styles.errorTooltip}
                  >{`Impeller trim must be between 0 and ${MAX_IMPELLER_TRIM} inches.`}</span>
                </div>
              )}
              {props.partNumber.includes("-3-") && (
                <li>
                  <div className={`${styles.row} row`}>
                    <div className={`col-12 col-lg-10 ${styles.cartAttribute}`}>
                      <span>Impeller Angle</span>
                    </div>
                    <div className="col-12 col-lg-9">
                      <input
                        className={`form-control mx-auto ${!props.impellerAngleIsValid ? styles.inputError : ""}`}
                        data-test="impeller-angle-input"
                        type="text"
                        maxLength={10}
                        value={updatedImpellerAngle || ""}
                        onChange={(e) => updateImpellerAngle(e.target.value)}
                        onBlur={() =>
                          validateCartItem(
                            props.cartItemId,
                            updatedQuantity,
                            props.paintColorId,
                            updatedImpellerTrim,
                            updatedImpellerAngle
                          )
                        }
                        onKeyUp={(e) => {
                          if (e.key === "Enter") {
                            validateCartItem(
                              props.cartItemId,
                              updatedQuantity,
                              props.paintColorId,
                              updatedImpellerTrim,
                              updatedImpellerAngle
                            );
                          }
                        }}
                      />
                    </div>
                  </div>
                </li>
              )}
              {!props.impellerAngleIsValid && (
                <div className="col-12 col-lg-9">
                  <span
                    className={styles.errorTooltip}
                  >{`Impeller angle must be between 0 and ${MAX_IMPELLER_ANGLE} degrees.`}</span>
                </div>
              )}
              <li>
                <div className={`${styles.row} row`}>
                  <div className={`col-12 col-lg-10 ${styles.cartAttribute}`}>
                    <span>Quantity ({props.units}):</span>
                  </div>
                  <div className="col-12 col-lg-9">
                    <input
                      className={`form-control mx-auto ${!props.quantityIsValid ? styles.inputError : ""}`}
                      data-test="item-quantity-input"
                      type="text"
                      maxLength={10}
                      value={updatedQuantity}
                      onChange={(e) => updateQuantity(e.target.value)}
                      onBlur={() =>
                        validateCartItem(
                          props.cartItemId,
                          updatedQuantity,
                          props.paintColorId,
                          updatedImpellerTrim,
                          updatedImpellerAngle
                        )
                      }
                      onKeyUp={(e) => {
                        if (e.key === "Enter") {
                          validateCartItem(
                            props.cartItemId,
                            updatedQuantity,
                            props.paintColorId,
                            updatedImpellerTrim,
                            updatedImpellerAngle
                          );
                        }
                      }}
                    />
                  </div>
                </div>
              </li>
              {!props.quantityIsValid &&
                (props.quoteId !== undefined && quantityIsBetweenOneAndMax ? (
                  <div className="col-12 col-lg-9">
                    <span
                      className={styles.errorTooltip}
                    >{`New quantity cannot be greater than current quantity on a saved quote.`}</span>
                  </div>
                ) : (
                  <div className="col-12 col-lg-9">
                    <span
                      className={styles.errorTooltip}
                    >{`Quantity must be a number between 1 and ${MAX_ITEM_QUANTITY}.`}</span>
                  </div>
                ))}
            </ul>
          </div>
          <div className="col-2 d-none d-lg-block"></div>
          <div className={`${styles.priceDeleteList} col-5 col-lg-6`}>
            <div>
              <div className="mb-2">
                {props.price !== null ? (
                  <Fragment>
                    <div className="row">
                      <div className={`${styles.priceDisplay}`}>List Price:</div>
                    </div>
                    <div className="row">
                      <div className={styles.priceAlign}>
                        <div className={styles.price} data-test="single-item-price-display">
                          $
                          {(Number(props.quantity) * props.price).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </div>
                      </div>
                    </div>
                  </Fragment>
                ) : (
                  <Fragment></Fragment>
                )}
              </div>
              <div>
                {props.price !== null ? (
                  <Fragment>
                    <div className="row">
                      <div className={`${styles.priceDisplay}`}>Discount Price:</div>
                    </div>
                    <div className="row">
                      <div className={styles.priceAlign}>
                        <div className={styles.price} data-test="single-item-discount-price-display">
                          ${getDiscountedPrice(props.price, props.quantity, props.companyDiscount)}
                        </div>
                      </div>
                    </div>
                  </Fragment>
                ) : (
                  <Fragment></Fragment>
                )}
              </div>
            </div>
            <div>
              <div className="row">
                <div className={styles.deleteButton}>
                  <button
                    className="btn btn-danger"
                    onClick={() => props.onDeleteCartItem(props.cartItemId)}
                    data-test="item-delete-button"
                  >
                    <i className="fa fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </li>
    </div>
  );
}

interface Props {
  isQuote: boolean;
  quoteId: string | undefined;
  cartItemId: number;
  name: string;
  partNumber: string;
  paintColorId: number | null;
  quantity: string;
  quantityIsValid: boolean;
  units: string;
  price: number | null;
  weight: number | null;
  companyDiscount: number | null;
  impellerTrim: string | null;
  impellerTrimIsValid: boolean;
  impellerAngle: string | null;
  impellerAngleIsValid: boolean;
  dateAvailable: string | null;
  isPaintable: boolean;
  paintColors: PaintColor[];
  onUpdateMultiplePaintCartItems: (itemId: number, newPaintColor: PaintColor | null) => void;
  onUpdateCartItem: (
    itemId: number,
    quantity: string,
    quantityIsValid: boolean,
    paintColorId: number | null,
    impellerTrim: string | null,
    impellerTrimIsValid: boolean,
    impellerAngle: string | null,
    impellerAngleIsValid: boolean
  ) => void;
  onUpdateCartItemDateAvailable: (itemId: number, dateAvailable: string | null) => void;
  onUpdateQuoteCartItemDateAvailable: (itemId: number, dateAvailable: string | null) => void;
  onDeleteCartItem: (itemId: number) => void;
}

interface PaintColor {
  paintColorId: number;
  hexCode: string;
  name: string;
}
